/**
 * 创建一个防抖函数，该函数会在延迟结束后调用最后一次传入的参数
 * @param fn - 要防抖的函数
 * @param wait - 延迟时间（毫秒）
 * @param options - 配置选项
 * @param options.leading - 是否在延迟开始前调用
 * @param options.trailing - 是否在延迟结束后调用
 * @returns 防抖后的函数，带有 cancel 方法用于取消延迟的函数调用
 * @example
 * ```ts
 * const debouncedFn = debounce((value) => console.log(value), 1000);
 * debouncedFn('test'); // 1秒后输出 'test'
 *
 * // 取消延迟的调用
 * debouncedFn.cancel();
 * ```
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  wait = 300,
  options: { leading?: boolean; trailing?: boolean } = {},
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: Parameters<T> | null = null;
  const { leading = false, trailing = true } = options;

  // 确保只能设置一个为 false
  if (!leading && !trailing) {
    throw new Error("leading 和 trailing 不能同时为 false");
  }

  const debounced = function (this: any, ...args: Parameters<T>) {
    // 保存最后一次调用的参数
    lastArgs = args;

    // 如果定时器不存在且开启了立即执行
    if (!timer && leading) {
      fn.apply(this, args);
    }

    // 清除之前的定时器
    if (timer) {
      clearTimeout(timer);
    }

    // 设置新的定时器
    timer = setTimeout(() => {
      if (trailing && lastArgs) {
        fn.apply(this, lastArgs);
      }
      timer = null;
      lastArgs = null;
    }, wait);
  } as ((...args: Parameters<T>) => void) & { cancel: () => void };

  // 添加取消方法
  debounced.cancel = function () {
    if (timer) {
      clearTimeout(timer);
      timer = null;
      lastArgs = null;
    }
  };

  return debounced;
}
